import {
  ArrowSquareOutIcon,
  BugIcon,
  ChartLineIcon,
  CloverIcon,
  EnvelopeIcon,
  FolderIcon,
  GameControllerIcon,
  GearIcon,
  ImagesIcon,
  ListChecksIcon,
  RocketLaunchIcon,
  SignOutIcon,
  SquaresFourIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import type { Permission } from "@lucky-try/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Link, type LinkProps, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import type { ComponentType } from "react";
import { authClient } from "../../lib/auth-client";
import { can, useMe } from "../../lib/session";
import { Button } from "../ui/button";
import { ReleaseStrip } from "./release-strip";

interface NavItem {
  to: LinkProps["to"];
  label: string;
  icon: ComponentType<{ className?: string }>;
  permission: Permission;
}

const sections: { title: string; items: NavItem[] }[] = [
  {
    title: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: SquaresFourIcon, permission: "dashboard.view" },
      { to: "/analytics", label: "Analytics", icon: ChartLineIcon, permission: "analytics.read" },
    ],
  },
  {
    title: "Catalog",
    items: [
      { to: "/games", label: "Games", icon: GameControllerIcon, permission: "catalog.read" },
      { to: "/categories", label: "Categories", icon: FolderIcon, permission: "catalog.read" },
      { to: "/media", label: "Media", icon: ImagesIcon, permission: "catalog.read" },
      {
        to: "/publishing",
        label: "Publishing",
        icon: RocketLaunchIcon,
        permission: "catalog.read",
      },
    ],
  },
  {
    title: "Inbox",
    items: [
      { to: "/reports", label: "Bug reports", icon: BugIcon, permission: "feedback.read" },
      { to: "/contact", label: "Contact", icon: EnvelopeIcon, permission: "feedback.read" },
    ],
  },
  {
    title: "System",
    items: [
      { to: "/users", label: "Users", icon: UsersIcon, permission: "users.read" },
      { to: "/settings", label: "Settings", icon: GearIcon, permission: "settings.read" },
      { to: "/audit", label: "Audit log", icon: ListChecksIcon, permission: "audit.read" },
    ],
  },
];

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL ?? "https://luckytrygames.com";

/** Where am I? Read from the path so the topbar always agrees with the rail. */
function useSectionLabel(): string {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  for (const section of sections) {
    for (const item of section.items) {
      const path = String(item.to);
      if (path === "/" ? pathname === "/" : pathname.startsWith(path)) return section.title;
    }
  }
  return "Console";
}

export function AppShell() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const sectionLabel = useSectionLabel();

  async function signOut() {
    await authClient.signOut();
    queryClient.clear();
    await navigate({ to: "/login" });
  }

  return (
    <div className="flex h-full">
      <aside className="border-line bg-surface flex w-58 shrink-0 flex-col border-r">
        <Link to="/" className="border-line flex h-14 items-center gap-2.5 border-b px-4">
          <span className="bg-accent text-accent-ink grid size-7 shrink-0 place-items-center rounded-full">
            <CloverIcon weight="bold" className="size-4" />
          </span>
          <span className="min-w-0">
            <span className="display text-ink block text-[15px] leading-none font-bold tracking-tight">
              LUCKY&nbsp;TRY
            </span>
            <span className="text-micro text-faint block font-semibold uppercase">Console</span>
          </span>
        </Link>

        <nav className="flex-1 overflow-y-auto px-2 py-4">
          {sections.map((section) => {
            const items = section.items.filter((item) => can(me, item.permission));
            if (!items.length) return null;
            return (
              <div key={section.title} className="mb-5 last:mb-0">
                <p className="text-micro text-faint px-3 pb-1.5 font-semibold uppercase">
                  {section.title}
                </p>
                <div className="flex flex-col gap-0.5">
                  {items.map((item) => (
                    <Link
                      key={String(item.to)}
                      to={item.to}
                      activeOptions={{ exact: item.to === "/" }}
                      className="rail-link group text-ui text-body hover:bg-raised hover:text-ink data-[status=active]:bg-accent-bg data-[status=active]:text-ink flex h-8 items-center gap-2.5 rounded-full px-3 font-medium"
                    >
                      <item.icon className="text-muted group-data-[status=active]:text-accent size-4 shrink-0" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <a
          href={siteUrl}
          target="_blank"
          rel="noreferrer"
          className="border-line text-caption text-muted hover:text-ink flex items-center gap-2 border-t px-4 py-3 transition-colors"
        >
          <ArrowSquareOutIcon className="size-3.5" /> Open luckytrygames.com
        </a>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-line bg-bg/85 sticky top-0 z-30 flex h-14 items-center gap-3 border-b px-6 backdrop-blur">
          <p className="text-micro text-faint font-semibold uppercase">{sectionLabel}</p>

          <div className="ml-auto flex items-center gap-3">
            <ReleaseStrip />
            <div className="border-line hidden h-8 items-center gap-2 rounded-full border pr-1 pl-3 sm:flex">
              <span className="text-caption text-body max-w-36 truncate font-medium">
                {me?.name}
              </span>
              <span className="text-micro text-accent-text bg-accent-bg rounded-full px-1.5 py-0.5 font-semibold uppercase">
                {me?.role}
              </span>
              <Button size="sm" variant="ghost" onClick={signOut} aria-label="Sign out">
                <SignOutIcon />
              </Button>
            </div>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1480px] px-6 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
